import React, { useMemo } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';

import {
  ROLE_ADMIN,
  ROLE_SALARY_ADMIN,
  ROLE_SALARY_VIEW,
  useAuth
} from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import { Collaborator } from '@/models';
import {
  buildExpensesView,
  useCollaboratorExpensesSummary,
} from '@/features/Collaborators/hooks/useCollaboratorExpensesSummary';

type CollaboratorsLayoutContext = {
  collaborators: Collaborator[];
  isLoading: boolean;
  error: Error | null;
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatCompetence = (competence?: string | null) => {
  if (!competence) return 'Competência';
  const normalized = competence.replace(/\D/g, '');
  if (normalized.length >= 6) {
    const year = normalized.slice(0, 4);
    const month = normalized.slice(4, 6);
    const date = new Date(Number(year), Number(month) - 1, 1);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    }
  }
  return competence;
};

export default function CollaboratorExpensesPage() {
  const { collaborators, isLoading, error } = useOutletContext<CollaboratorsLayoutContext>();
  const { id } = useParams<{ id: string }>();
  const { user, userHasRole } = useAuth();
  const collaborator = useMemo(
    () => collaborators.find((item) => item.id === id),
    [collaborators, id],
  );
  const { expensesSummary, isLoading: isExpensesLoading } = useCollaboratorExpensesSummary(collaborator);
  const expensesView = buildExpensesView(expensesSummary);

  const canSeePage =
    userHasRole(ROLE_ADMIN) ||
    userHasRole(ROLE_SALARY_ADMIN) ||
    userHasRole(ROLE_SALARY_VIEW);

  if (!canSeePage) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-900">
        Você não tem permissão para visualizar os detalhes deste colaborador.
      </div>
    );
  }

  if (isLoading && !collaborators.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-800">
        Não foi possível carregar o colaborador.
      </div>
    );
  }

  if (!collaborator) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-700 shadow-sm">
        Colaborador não encontrado.
      </div>
    );
  }

  const rows = [
    { label: 'Plano de Saúde', exp: expensesSummary?.benefitsBreakdown?.healthPlan, avg: expensesView.averagesBenefits.healthPlan, calc: expensesView.calcBenefitsBreakdown?.healthPlan, bold: false },
    { label: 'Promoção de Saúde', exp: expensesSummary?.benefitsBreakdown?.healthPromotion, avg: expensesView.averagesBenefits.healthPromotion, calc: undefined, bold: false },
    { label: 'Alimentação', exp: expensesSummary?.benefitsBreakdown?.foodBenefit, avg: expensesView.averagesBenefits.foodBenefit, calc: expensesView.calcBenefitsBreakdown?.mealAllowance, bold: false },
    { label: 'Transporte', exp: undefined, avg: expensesView.averagesBenefits.transportation, calc: expensesView.calcBenefitsBreakdown?.transportation, bold: false },
    { label: 'Funerário', exp: expensesSummary?.benefitsBreakdown?.funeralAssistance, avg: expensesView.averagesBenefits.funeralAssistance, calc: undefined, bold: false },
    { label: 'Pet', exp: expensesSummary?.benefitsBreakdown?.petPlan, avg: expensesView.averagesBenefits.petPlan, calc: undefined, bold: false },
    { label: 'Educação Financeira', exp: undefined, avg: expensesView.averagesBenefits.financialEducation, calc: expensesView.calcBenefitsBreakdown?.financialEducation, bold: false },
    { label: 'Subtotal de Benefícios', exp: expensesView.expensesBenefitsSubtotal, avg: expensesView.avgBenefitsSubtotal, calc: expensesView.calcBenefitsDisplay, bold: true, showDelta: true },
    { label: 'Salário Base', exp: expensesView.baseSalaryValue, avg: expensesView.avgBase, calc: expensesView.calcSalary, bold: false },
    { label: 'FGTS', exp: expensesView.fgtsValue, avg: expensesView.avgFgts, calc: expensesView.calcLaborBreakdown?.fgts, bold: false },
    { label: 'Encargos', exp: expensesView.chargesValue, avg: expensesView.avgCharges, calc: expensesView.calcLaborBreakdown?.inss, bold: false },
    {
      label: 'Subtotal de Encargos',
      exp: expensesView.expensesEncargosSubtotal,
      avg: expensesView.avgEncumbrancesSubtotal,
      calc: expensesView.calcEncargosSubtotal,
      bold: true,
      showDelta: true,
    },
    { label: '13º Salário', exp: expensesView.provisions.thirteenth, avg: expensesView.avgProvisions.thirteenth, calc: expensesView.calcProvisions.thirteenth, bold: false },
    { label: 'Férias', exp: expensesView.provisions.vacation, avg: expensesView.avgProvisions.vacation, calc: expensesView.calcProvisions.vacation, bold: false },
    { label: '1/3 Constitucional', exp: expensesView.provisions.constitutional, avg: expensesView.avgProvisions.constitutional, calc: expensesView.calcProvisions.constitutional, bold: false },
    { label: 'FGTS sobre férias', exp: expensesView.provisions.fgtsVacation, avg: expensesView.avgProvisions.fgtsVacation, calc: expensesView.calcProvisions.fgtsVacation, bold: false },
    { label: 'FGTS sobre 13º', exp: expensesView.provisions.fgtsThirteenth, avg: expensesView.avgProvisions.fgtsThirteenth, calc: expensesView.calcProvisions.fgtsThirteenth, bold: false },
    { label: 'Multa FGTS', exp: expensesView.provisions.fgtsPenalty, avg: expensesView.avgProvisions.fgtsPenalty, calc: expensesView.calcProvisions.fgtsPenalty, bold: false },
    { label: 'Aviso Prévio', exp: undefined, avg: undefined, calc: expensesView.calcLaborBreakdown?.notice, bold: false },
    { label: 'Auxílio Doença', exp: undefined, avg: undefined, calc: expensesView.calcLaborBreakdown?.sickLeave, bold: false },
    { label: 'Licença Paternidade', exp: undefined, avg: undefined, calc: expensesView.calcLaborBreakdown?.paternityLeave, bold: false },
    { label: 'PL', exp: undefined, avg: undefined, calc: expensesView.calcLaborBreakdown?.profitSharing, bold: false },
    { label: 'Subtotal de provisões', exp: expensesView.provisionsTotal, avg: expensesView.avgProvisionsTotal, calc: expensesView.calcProvisionsTotal, bold: true, showDelta: true },
    { label: 'Total', exp: expensesView.expensesTotal, avg: expensesView.avgTotal, calc: expensesView.calcTotal, bold: true, showDelta: true },
    {
      label: 'Custo hora (168h)',
      exp: expensesView.hourlyCost,
      avg: expensesView.avgHourlyCost,
      calc: expensesSummary?.calculatorTotals?.hourlyCost ?? expensesSummary?.calculator?.hourlyCost,
      bold: true,
      showDelta: true,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-4 md:p-6 shadow-[0_40px_120px_-60px_rgba(45,55,72,0.35)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-700">Despesas</p>
              <p className="text-xl font-semibold text-slate-900">
                {formatCompetence(expensesSummary?.competence)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/collaborators/${collaborator.id}`}
                className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
              >
                Voltar para o perfil
              </Link>
            </div>
          </div>

          <div className="overflow-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold text-slate-800">Item</th>
                  <th className="px-4 py-3 font-semibold text-slate-800">
                    {formatCompetence(expensesSummary?.competence)}
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-800">Média da competência</th>
                  <th className="px-4 py-3 font-semibold text-slate-800">Calculadora</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => (
                  <tr key={row.label} className={row.bold ? 'bg-slate-50' : undefined}>
                    <td className="px-4 py-2 text-slate-900">
                      <span className={row.bold ? 'font-semibold' : ''}>{row.label}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-800">
                      <span className={row.bold ? 'font-semibold' : ''}>
                        {formatCurrency(row.exp)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-800">
                      <span className={row.bold ? 'font-semibold' : ''}>
                        {row.avg === undefined && !row.bold ? '-' : formatCurrency(row.avg)}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-slate-800">
                      <span className={row.bold ? 'font-semibold' : ''}>
                        {formatCurrency(row.calc)}
                        {row.showDelta
                          ? (() => {
                            if (
                              row.exp === undefined ||
                              row.exp === null ||
                              row.calc === undefined ||
                              row.calc === null
                            ) {
                              return null;
                            }
                            if (row.exp === 0) return null;
                            const percent = ((row.calc - row.exp) / row.exp) * 100;
                            const className =
                              percent > 0 ? 'text-emerald-600' : percent < 0 ? 'text-rose-600' : 'text-slate-500';
                            const prefix = percent > 0 ? '+' : '';
                            return (
                              <span className={`ml-2 text-xs ${className}`}>
                                ({`${prefix}${percent.toFixed(1)}%`})
                              </span>
                            );
                          })()
                          : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {isExpensesLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
